import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {Badge} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/badge.types';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {useTranslation} from '@aboutyou/i18n';

export const useCampaignAndSaleBadgeTextByProduct = ({
  product,
  selectedSizeId,
  badges,
}: {
  product: Product;
  selectedSizeId?: number;
  badges: Badge[];
}) => {
  const {t} = useTranslation();
  const {isActive: isPriceDisplayOverhaulActive} = useIsFeatureActive(
    'price_display_overhaul',
  );

  const availableSizes = product.sizes.filter(size => size.quantity > 0);

  const {defaultSaleBadgeLabel, defaultCampaignBadgeLabel} =
    getDefaultSaleAndCampaignLabels(badges);

  const sale = (() => {
    if (!defaultSaleBadgeLabel) {
      return null;
    }

    if (selectedSizeId) {
      const selectedSize = availableSizes.find(
        size => size.id === selectedSizeId,
      );

      if (!selectedSize?.price?.saleReduction) {
        return null;
      }

      return t('-{reductionPercentage}%', {
        reductionPercentage:
          selectedSize.price.saleReduction.reductionPercentage,
      });
    }

    return defaultSaleBadgeLabel;
  })();

  const campaignSale = (() => {
    if (!defaultCampaignBadgeLabel) {
      return null;
    }

    if (selectedSizeId) {
      const selectedSize = availableSizes.find(
        size => size.id === selectedSizeId,
      );

      if (!selectedSize?.price?.campaignReduction) {
        return null;
      }

      if (isPriceDisplayOverhaulActive) {
        return t('-{reductionPercentage}% DEAL', {
          reductionPercentage:
            selectedSize.price.campaignReduction.reductionPercentage,
        });
      }

      return t('-{reductionPercentage}% EXTRA', {
        reductionPercentage:
          selectedSize.price.campaignReduction.reductionPercentage,
      });
    }

    return defaultCampaignBadgeLabel;
  })();

  return {
    sale,
    campaignSale,
  };
};

const getDefaultSaleAndCampaignLabels = (badges: Badge[]) => {
  let defaultSaleBadgeLabel = '';
  let defaultCampaignBadgeLabel = '';

  for (const badge of badges) {
    if (badge.type?.$case === 'sale') {
      defaultSaleBadgeLabel = badge.type.sale.defaultLabel;
    } else if (badge.type?.$case === 'campaignSale') {
      defaultCampaignBadgeLabel = badge.type.campaignSale.defaultLabel;
    }
  }

  return {
    defaultSaleBadgeLabel,
    defaultCampaignBadgeLabel,
  };
};
